/**
 * useSocket — singleton Socket.IO client
 * Il Diario di Bordo - Frontend
 *
 * Connessione singola condivisa per tutta l'app:
 *  - autenticazione via cookie httpOnly (withCredentials: true)
 *  - riconnessione automatica
 *  - subscribe a eventi specifici tramite `useSocketEvent(event, handler)`
 *
 * Da usare in App.jsx (wrapper) o nei singoli componenti che ascoltano.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { io } from 'socket.io-client'

let sharedSocket = null

const getSocketUrl = () => {
  // VITE_SOCKET_URL va settata negli Environment Variables di Vercel.
  // In dev locale, fallback a localhost:3001 (server Express dev).
  return import.meta.env.VITE_SOCKET_URL || 'http://localhost:3001'
}

const getSocket = () => {
  if (sharedSocket) return sharedSocket
  sharedSocket = io(getSocketUrl(), {
    withCredentials: true,
    transports: ['websocket', 'polling'],
    autoConnect: false,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity
  })
  return sharedSocket
}

/**
 * Hook principale: ritorna il socket condiviso, lo stato di connessione e
 * un helper per ascoltare eventi (auto-cleanup su unmount).
 */
export function useSocket({ enabled = true } = {}) {
  const socket = getSocket()
  const [connected, setConnected] = useState(socket.connected)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!enabled) return

    const handleConnect = () => {
      setConnected(true)
      setError(null)
    }
    const handleDisconnect = () => setConnected(false)
    const handleError = (err) => setError(err?.message || 'Errore connessione')

    socket.on('connect', handleConnect)
    socket.on('disconnect', handleDisconnect)
    socket.on('connect_error', handleError)

    if (!socket.connected) {
      socket.connect()
    }

    return () => {
      socket.off('connect', handleConnect)
      socket.off('disconnect', handleDisconnect)
      socket.off('connect_error', handleError)
    }
  }, [enabled, socket])

  const emit = useCallback((event, ...args) => {
    if (!socket.connected) return Promise.reject(new Error('Socket non connesso'))
    return new Promise((resolve) => {
      socket.emit(event, ...args, (ack) => resolve(ack))
    })
  }, [socket])

  return { socket, connected, error, emit }
}

/**
 * Ascolta un evento specifico con cleanup automatico.
 */
export function useSocketEvent(event, handler, { enabled = true } = {}) {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    if (!enabled || !event) return
    const socket = getSocket()
    const wrapped = (...args) => handlerRef.current?.(...args)
    socket.on(event, wrapped)
    return () => socket.off(event, wrapped)
  }, [event, enabled])
}

/**
 * Disconnessione esplicita (usata al logout).
 */
export function disconnectSocket() {
  if (sharedSocket) {
    sharedSocket.disconnect()
  }
}
